import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, Menu, message, Tabs } from "antd";
import { useParams } from "react-router-dom";
import { svg } from "../../../../Assets/svg";
import { RootState, useAppDispatch, useAppSelector } from "../../../../store";
import {
  DefaultProfileImage,
  getFullAwsUrl,
  momentDDMMYY,
  CapitalizeFirstWord,
} from "../../../../helpers/utils";
import { decryptByText } from "../../../../helpers/bycrypt";
import {
  GetMezzanineOrdersDispatchPlanningDetailsAPI,
  UpdateMezzanineOrderDispatchPlaningLoadAPI,
  UpdateMezzanineOrderDispatchStatusAPI,
  UpdateMezzanineOrderDispatchDetailsAPI,
  AddMezzanineOrderDispatchTeamAPI,
} from "../../../Redux/orders";
import FullScreenLoader from "../../../Components/FullScreenLoader";
import AddDispatchLoadModal from "./Modals/AddDispatchLoadModal";
import AddDispatchTeamModal from "./Modals/AddDispatchTeamModal";
import DispatchDetailsModal from "./Modals/DispatchDetailsModal";
import FillDispatchChecklistModal from "./Modals/FillDispatchChecklistModal";
import ViewDispatchChecklistModal from "./Modals/ViewDispatchChecklistModal";
import { DispatchPlaningStatusArray } from "../orderTypes";

const MezzanineDispatchPlanning: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const { orderDetails, mezzanineDispatchPlaningDetails } = useAppSelector(
    (state: RootState) => state.orders,
  );

  const { getMezzanineDispatchPlaningDetailsLoading } = useAppSelector(
    (state: RootState) => state.loading,
  );

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [addIsLoadModal, setAddIsLoadModal] = useState(false);
  const [isEditLoadData, setIsEditLoadData] = useState<any>(null);

  const [isDispatchDetails, setIsDispatchDetails] = useState(false);
  const [isDispatchDetailsData, setIsDispatchDetailsData] = useState<any>(null);

  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [selectedLoadData, setSelectedLoadData] = useState<any>(null);
  const [isDispatchChecklistModalOpen, setIsDispatchChecklistModalOpen] = useState(false);
  const [isViewDispatchChecklistModalOpen, setIsViewDispatchChecklistModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(GetMezzanineOrdersDispatchPlanningDetailsAPI({ order_id: id }));
      dispatch({ type: "CLEAR_TEAM_MEMBER_ARRAY" });
    }
  }, [id, dispatch]);

  const allLoads = useMemo(() => {
    return mezzanineDispatchPlaningDetails?.load_id || [];
  }, [mezzanineDispatchPlaningDetails]);

  useEffect(() => {
    if (!allLoads || allLoads.length === 0) {
      setSelectedLoadData(null);
      setActiveKey(null);
      return;
    }

    const firstLoad = allLoads[0];
    const hasActiveLoad = allLoads.some(
      (elem: any) => String(elem?._id) === String(activeKey),
    );

    const nextActiveKey = hasActiveLoad ? activeKey : firstLoad._id;
    const currentLoad =
      allLoads.find(
        (elem: any) => String(elem?._id) === String(nextActiveKey),
      ) || firstLoad;

    setActiveKey(nextActiveKey);
    setSelectedLoadData(currentLoad);
  }, [allLoads, activeKey]);

  const handleUpdateStatus = (status: string) => {
    if (!id || !selectedLoadData?._id) return;
    dispatch(
      UpdateMezzanineOrderDispatchStatusAPI({
        order_id: id,
        load_id: selectedLoadData._id,
        status,
      }),
    );
  };

  const handleSaveLoadCount = (payload: any, callback: () => void) => {
    dispatch(UpdateMezzanineOrderDispatchPlaningLoadAPI(payload, callback));
  };

  const handleSaveTeam = (payload: any, callback: (data: any) => void) => {
    dispatch(AddMezzanineOrderDispatchTeamAPI(payload, callback));
  };

  const handleSaveDetails = (payload: any, callback: () => void) => {
    dispatch(UpdateMezzanineOrderDispatchDetailsAPI(payload, callback));
  };

  const MoreOptionsMenu = () => {
    return (
      <Menu>
        <Menu.Item
          key="1"
          onClick={() => {
            handleUpdateStatus("init");
          }}
        >
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            Mark As Undispatched
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  return getMezzanineDispatchPlaningDetailsLoading ? (
    <div
      className="w-100 d-flex align-items-center justify-content-center"
      style={{ height: "calc(100vh - 12rem)" }}
    >
      <FullScreenLoader />
    </div>
  ) : (
    <div className="h-100">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-md-3 mb-2">
        <h4 className="fs-5 fw-bold text-black mb-0">
          Mezzanine Floor Dispatch Planning
        </h4>
        {mezzanineDispatchPlaningDetails ? (
          <h4 className="fs-5 fw-bold text-black mb-0 gap-md-3 d-flex align-items-center gap-2 ms-auto">
            No of Load: {mezzanineDispatchPlaningDetails?.load_number}{" "}
            {DispatchPlaningStatusArray.includes(orderDetails?.order_status) ? (
              <span
                className="svg-32 cursor-pointer"
                onClick={() => {
                  setIsEditLoadData({
                    loads: mezzanineDispatchPlaningDetails?.load_number,
                  });
                  setAddIsLoadModal(true);
                }}
              >
                {svg?.tableEditIcon}
              </span>
            ) : (
              ""
            )}
          </h4>
        ) : (
          ""
        )}
      </div>

      {!mezzanineDispatchPlaningDetails ? (
        <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white flex-fill">
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center">
            <div>{svg?.dispatchTruckIcon}</div>

            <h5 className="fs-5 fw-bold text-black ">
              Number of Load / Number of Vehicle not added yet!
            </h5>
            <p className="fs-6 text-4a4a4a ">
              Transport details are incomplete. Kindly add the number of loads
              or vehicles required before continuing.
            </p>
            {DispatchPlaningStatusArray.includes(orderDetails?.order_status) ? (
              <div>
                <button
                  className="btn btn-lg btn-primary"
                  onClick={() => {
                    setIsEditLoadData(null);
                    setAddIsLoadModal(true);
                  }}
                >
                  ADD LOAD
                </button>
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-md-4 mb-3 gap-2">
            <div className="customHorizontalTabs bg-white overflow-hidden border-0 rounded-3 d-inline-block mw-100">
              <Tabs
                activeKey={activeKey || undefined}
                onChange={(key: string) => {
                  const newData =
                    mezzanineDispatchPlaningDetails?.load_id?.find(
                      (elem: any) => elem?._id === key,
                    );
                  setActiveKey(key);
                  setSelectedLoadData(newData);
                }}
                items={mezzanineDispatchPlaningDetails?.load_id?.map(
                  (load: any) => ({
                    label: `Load ${load.load_number}`,
                    key: load._id,
                  }),
                )}
              />
            </div>

            {DispatchPlaningStatusArray.includes(orderDetails?.order_status) ? (
              selectedLoadData?.status === "dispatched" ? (
                <div className="d-flex align-items-center gap-2 ms-auto">
                  <span
                    className={
                      "d-inline-flex align-items-center fw-semibold fs-6 svg-10 quotation-status statusDraft "
                    }
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {svg.dotIcon}
                    <span className="ms-1 d-inline-block lh-normal">
                      Dispatched
                    </span>
                  </span>
                  <Dropdown
                    overlay={() => MoreOptionsMenu()}
                    trigger={["click"]}
                    placement="bottomRight"
                    className="cursor-pointer"
                    overlayClassName="moreOptionDropdown"
                  >
                    <span className="svg-32 svg-outline-primary">
                      {svg?.tableMoreOption}
                    </span>
                  </Dropdown>
                </div>
              ) : (
                selectedLoadData?._id && (
                  <div>
                    <button
                      className={`btn btn-lg btn-lg-mob btn-primary`}
                      onClick={() => {
                        if (!selectedLoadData?.dispatched_details) {
                          message.error("please enter dispatched details");
                          return;
                        }
                        const dispatchChecklist =
                          selectedLoadData?.dispatch_checklist || [];
                        const questions = dispatchChecklist.flatMap(
                          (c: any) => c.questions_data || [],
                        );
                        if (
                          dispatchChecklist.length > 0 &&
                          questions.length > 0
                        ) {
                          setIsDispatchChecklistModalOpen(true);
                        } else {
                          handleUpdateStatus("dispatched");
                        }
                      }}
                    >
                      Mark As Dispatched
                    </button>
                  </div>
                )
              )
            ) : (
              ""
            )}
          </div>

          {selectedLoadData?.status === "dispatched" &&
            (selectedLoadData?.dispatch_checklist || []).flatMap(
              (c: any) => c.questions_data || [],
            ).length > 0 && (
              <div className="mb-4">
                <div className="card border-0 shadow-sm rounded-4 bg-white">
                  <div className="card-body p-md-4 p-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <h4 className="fs-5 fw-bold text-black mb-0">
                      Load {selectedLoadData?.load_number} Dispatch Checklist Details
                    </h4>
                    <button
                      className="btn btn-primary"
                      onClick={() => setIsViewDispatchChecklistModalOpen(true)}
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            )}

          {/* Load Team Section */}
          <div className="mb-4">
            <div className="mb-md-3 mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
              <h4 className="fs-5 fw-bold text-black mb-0">
                Load {selectedLoadData?.load_number} Dispatch Team
              </h4>
              {DispatchPlaningStatusArray.includes(
                orderDetails?.order_status,
              ) ? (
                selectedLoadData?.status === "dispatched" ? (
                  ""
                ) : (
                  <span
                    className="fw-bold fs-6 cursor-pointer text-primary text-decoration-underline"
                    onClick={() => setDispatchModalOpen(true)}
                  >
                    Modify
                  </span>
                )
              ) : (
                ""
              )}
              {dispatchModalOpen && (
                <AddDispatchTeamModal
                  visible={dispatchModalOpen}
                  onClose={() => setDispatchModalOpen(false)}
                  selectedLoadData={selectedLoadData}
                  setSelectedLoadData={setSelectedLoadData}
                  employeList={
                    selectedLoadData?.employee_ids?.length > 0
                      ? selectedLoadData?.employee_ids?.map((elem: any) =>
                          typeof elem === "object" ? elem?._id : elem,
                        )
                      : []
                  }
                  onSave={handleSaveTeam}
                />
              )}
            </div>
            <div className="card border-0 shadow-sm rounded-4 bg-white">
              <div className="card-body d-flex flex-wrap gap-md-4 gap-3 p-md-4 p-3">
                {selectedLoadData?.employee_ids?.length > 0 ? (
                  selectedLoadData?.employee_ids?.map((member: any) => (
                    <div
                      key={member?._id || member}
                      className="d-flex align-items-center gap-2"
                    >
                      <div className="img-34">
                        <img
                          src={
                            member?.profile_pic
                              ? getFullAwsUrl(member?.profile_pic)
                              : DefaultProfileImage
                          }
                          alt="dipatch team"
                          className="rounded-circle object-fit-cover w-100 h-100"
                          width={34}
                          height={34}
                          onError={(e: any) => {
                            e.target.onerror = null;
                            e.target.src = DefaultProfileImage;
                          }}
                        />
                      </div>
                      <h5 className="mb-0 fw-semibold fs-5 mob-fs-5 text-break">
                        {`${CapitalizeFirstWord(
                          decryptByText(member?.first_name || ""),
                        )} ${CapitalizeFirstWord(
                          decryptByText(member?.last_name || ""),
                        )}`}
                      </h5>
                    </div>
                  ))
                ) : (
                  <div className="d-flex align-items-center justify-content-center gap-2 text-949494 w-100">
                    No dispatch team added yet
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Load Details Section */}
          <div className="mb-4">
            <div className="mb-md-3 mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
              <h4 className="fs-5 fw-bold text-black mb-0">
                Load {selectedLoadData?.load_number} Details
              </h4>
              {DispatchPlaningStatusArray.includes(
                orderDetails?.order_status,
              ) ? (
                <span
                  className={`fw-bold fs-6 cursor-pointer text-primary text-decoration-underline ${
                    selectedLoadData?.status === "dispatched"
                      ? "text-secondary"
                      : "text-primary"
                  }`}
                  onClick={() => {
                    if (selectedLoadData?.status === "dispatched") return;
                    setIsDispatchDetails(true);
                    setIsDispatchDetailsData(selectedLoadData);
                  }}
                >
                  Modify
                </span>
              ) : (
                ""
              )}
            </div>
            <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
              <div className="row gy-md-4 gy-3">
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Dispatch Date
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {selectedLoadData?.dispatched_details?.dispatched_at
                      ? momentDDMMYY(
                          selectedLoadData?.dispatched_details?.dispatched_at,
                        )
                      : "-"}
                  </span>
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Tracking Number
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {selectedLoadData?.dispatched_details?.tracking_number ||
                      "-"}
                  </span>
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Transporter
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {selectedLoadData?.dispatched_details?.transporter || "-"}
                  </span>
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Vehicle Info
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {selectedLoadData?.dispatched_details?.vehicle_info || "-"}
                  </span>
                </div>

                <div className="col-12">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Dispatch Notes
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {selectedLoadData?.dispatched_details?.note || "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {addIsLoadModal && (
        <AddDispatchLoadModal
          visible={addIsLoadModal}
          setIsModalVisible={setAddIsLoadModal}
          setDispatchPlaning={() => {}}
          isEditLoadData={isEditLoadData}
          setActiveKey={setActiveKey}
          onSave={handleSaveLoadCount}
        />
      )}

      {isDispatchDetails && (
        <DispatchDetailsModal
          visible={isDispatchDetails}
          setIsModalVisible={setIsDispatchDetails}
          isDispatchDetailsData={isDispatchDetailsData}
          setIsDispatchDetailsData={setIsDispatchDetailsData}
          onSave={handleSaveDetails}
        />
      )}

      {isDispatchChecklistModalOpen && (
        <FillDispatchChecklistModal
          visible={isDispatchChecklistModalOpen}
          setIsModalVisible={setIsDispatchChecklistModalOpen}
          selectedLoadData={selectedLoadData}
          onSave={(formattedChecklist: any) => {
            setIsDispatchChecklistModalOpen(false);
            if (!id || !selectedLoadData?._id) return;
            dispatch(
              UpdateMezzanineOrderDispatchStatusAPI({
                order_id: id,
                load_id: selectedLoadData._id,
                status: "dispatched",
                dispatch_checklist: formattedChecklist,
              }),
            );
          }}
        />
      )}

      {isViewDispatchChecklistModalOpen && (
        <ViewDispatchChecklistModal
          visible={isViewDispatchChecklistModalOpen}
          setIsModalVisible={setIsViewDispatchChecklistModalOpen}
          selectedLoadData={selectedLoadData}
        />
      )}
    </div>
  );
};

export default MezzanineDispatchPlanning;
